import dataLayer from '@itbpbg/lms-components/src/store/plugins/dataLayer';
import banners from '@itbpbg/lms-components/src/store/plugins/banners';
import Cookies from 'js-cookie';
import auth from './plugins/auth';
import tracking from './plugins/tracking';

export const state = () => ({
  filters: null,
  breakPointLayoutResolution: 1024,
  layout: 'desktop',
  tablet: false,
  laptop: false,
  loader: false,
  copy: null,
  campaign: false,
  downloadAssignment: false,
  curriculumExpanded: true,
  referrer: null,
  trustpilotStats: { reviews: null, score: null, lastScrapedAt: null },
});

export const plugins = [
  banners,
  dataLayer,
  auth,
  tracking,
];

export const mutations = {
  setReferrer(state, payload) {
    state.referrer = payload;
  },
  setTrustpilotStats(state, stats) {
    state.trustpilotStats = stats || { reviews: null, score: null, lastScrapedAt: null };
  },
  setFilters(state, payload) {
    state.filters = { ...payload };
  },
  setLayout(state, layout) {
    state.layout = layout;
  },
  setLayoutTablet(state, condition) {
    state.tablet = condition;
  },
  setLayoutLaptop(state, condition) {
    state.laptop = condition;
  },
  loader(state, visible) {
    state.loader = visible;
  },
  setCopy(state, copy) {
    state.copy = copy;
  },
  setCampaign(state, campaign) {
    state.campaign = campaign;
  },
  changeDownloadPermission(state, condition) {
    state.downloadAssignment = condition;
  },
  setCurriculumExpanded(state, condition) {
    state.curriculumExpanded = condition;
    if (process.client) {
      Cookies.set('lmsCurriculumExpanded', condition);
    }
  },
};

export const getters = {
};

export const actions = {
  async fetchTrustpilotStats({ commit, state }, platform) {
    if (state.trustpilotStats && state.trustpilotStats.reviews !== null && state.trustpilotStats.score !== null) {
      return state.trustpilotStats;
    }

    try {
      // Use $axios if available (Nuxt context), fallback to global fetch
      let stats;
      if (this.$axios) {
        stats = await this.$axios.$get(`/stats/trustpilot/${platform}`);
      } else {
        const res = await fetch(`/stats/trustpilot/${platform}`);
        stats = await res.json();
      }
      commit('setTrustpilotStats', stats);
      return stats;
    } catch (e) {
      commit('setTrustpilotStats', { reviews: null, score: null, lastScrapedAt: null });
      return { reviews: null, score: null, lastScrapedAt: null };
    }
  },
  async nuxtServerInit({ dispatch, commit }, {
    req, res, route, redirect, $axios, $config,
  }) {
    try {
      const platform = $config?.platform || (process.env.PLATFORM) || 'ds';
      const stats = await dispatch('fetchTrustpilotStats', platform);
      commit('setTrustpilotStats', stats);
    } catch (e) {
      commit('setTrustpilotStats', { reviews: null, score: null, lastScrapedAt: null });
    }

    /**
     * Linkedin social signup / login
     */
    if (route.query.code) {
      let routePath = route.path.replace(/^\/|\/$/g, '');
      routePath = ['checkout', 'resources-center/exams/signup', 'flashcards'].includes(routePath) ? routePath : 'login';
      let queryParams = route.query?.state?.replace(/,/gm, '&');

      if (routePath.includes('checkout')) {
        queryParams += '&checkout=true';
      }

      commit('loader', true);
      const request = await $axios.post(`/auth/social/linkedin?keepMeLoggedIn=true&beta=${!!$config.gateway}&${queryParams}`, {
        token: route.query.code,
        route: routePath,
      }).catch(() => ({ }));

      if (request.data && request.data.businessAccount) {
        dispatch('auth/setUserInfo', request.data);
        return redirect(`/${routePath}${queryParams ? `/?${queryParams}&error=business` : '/?error=business'}`);
      }

      if (request.data && request.data.user) {
        dispatch('auth/setUserInfo', request.data.user);
        res.setHeader('Set-Cookie', request.headers['set-cookie']);
        if (request.data.user.registered) {
          return redirect(`/redirecting/?continue=${routePath}${queryParams ? `/?${queryParams}` : '/'}`);
        }

        return redirect(`/${routePath}${queryParams ? `/?${queryParams}` : '/'}`);
      }
    }

    if (req.headers.cookie) {
      const parsedCookies = req.headers.cookie.split(';').reduce((p, c) => {
        const [k, v] = c.split('=');

        return { ...p, [k.trim()]: decodeURIComponent(v) };
      }, { });

      const {
        user,
        refreshToken,
        accessTokenExpire,
        refreshTokenExpire,
        hideBanner,
        abTests,
      } = parsedCookies;

      if (parsedCookies['_365referrer-lms']) {
        commit('setReferrer', parsedCookies['_365referrer-lms']);
      }

      commit('banners/toggle', hideBanner);

      if (abTests && abTests !== 'undefined') {
        // commit('tests/addUserTests', JSON.parse(abTests));
      }

      if (refreshToken) {
        let userInfo = null;
        try {
          userInfo = JSON.parse(user).user;
        } catch (e) {
          return false;
        }

        if (refreshToken && userInfo) {
          const checkAuth = await dispatch('auth/checkAuth', { accessTokenExpire, refreshTokenExpire });

          if (checkAuth) {
            dispatch('auth/setUserInfo', userInfo);
          }

          if (typeof checkAuth === 'object') {
            const newCookies = checkAuth.reduce((p, c) => (`${p}; ${c.split(';')[0]}`), '');

            req.headers.cookie = req.headers.cookie
              .replace(/accessToken=[^;]+;/g, '')
              .replace(/accessTokenExpire=[^;]+;/g, '')
              .replace(/user=[^;]+;/g, '') + newCookies;
            res.setHeader('Set-Cookie', checkAuth);
          }

          return true;
        }
      }

      return false;
    }

    return false;
  },
  async fetchQuestions(store, { page = 1, limit = 10, search = '' }) {
    const allQuestions = await this.$axios.$get(`/qa-hub/questions?page=${page}&limit=${limit}&search=${search}`);

    if (allQuestions) {
      const { questions, ...rest } = allQuestions;
      const rows = allQuestions.questions.map((e) => ({
        ...e, date: new Date(e.date).toLocaleString(),
      }));
      return { ...rest, rows };
    }

    return {};
  },
  setReferrer({ commit }, payload) {
    const fullUrl = payload && payload.startsWith('http') ? payload : `${this.$config.appUrl}${payload}`;

    if (payload && payload.includes('redirecting')) {
      return;
    }

    commit('setReferrer', fullUrl);
  },
};
